import { Package, Clock, Gift, Phone, Timer, Zap, Loader2 } from 'lucide-react';
import { Contact, LeadTemperature } from '@/types/crm';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLeadScore } from '@/hooks/useLeadScore';
import { usePersona } from '@/hooks/usePersona';


// WhatsApp icon component
const WhatsAppIcon = ({ size = 11, className }: { size?: number; className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

type ViewMode = 'kanban' | 'list';

interface LeadCardProps {
  contact: Contact;
  onClick?: () => void;
  viewMode?: ViewMode;
  stageColor?: string;
  clientId?: string;
  isClosedSale?: boolean;
}

const getScoreStyle = (score: number) => {
  if (score >= 70) return { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 0.4)', color: '#22C55E' };
  if (score >= 40) return { bg: 'rgba(245, 158, 11, 0.2)', border: 'rgba(245, 158, 11, 0.4)', color: '#F59E0B' };
  return { bg: 'rgba(148, 163, 184, 0.2)', border: 'rgba(148, 163, 184, 0.4)', color: '#94A3B8' };
};

const temperatureConfig: Record<LeadTemperature, { emoji: string; className: string; color: string }> = {
  quente: { emoji: '🔥', className: 'bg-temp-hot/20 text-temp-hot border-temp-hot/40', color: '#EF4444' },
  morno: { emoji: '🌤️', className: 'bg-temp-warm/20 text-temp-warm border-temp-warm/40', color: '#F59E0B' },
  frio: { emoji: '❄️', className: 'bg-temp-cold/20 text-temp-cold border-temp-cold/40', color: '#3B82F6' }
};

// Helper to format time ago
const getTimeAgo = (dateString?: string): string | null => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  } catch {
    return null;
  }
};

// Helper to get time in stage based on last update date
const getTimeInStage = (contact: Contact): string => {
  const dateStr = contact.data_ultima_atualizacao || contact.atualizado_em || contact.updated_at || contact.data_primeira_mensagem || contact.criado_em || contact.created_at || '';
  if (!dateStr) return 'Recém-adicionado';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Recém-adicionado';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;
    
    if (diffDays > 0) {
      return `${diffDays}d ${remainingHours}h`;
    }
    if (diffHours > 0) {
      return `${diffHours}h`;
    }
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return diffMinutes > 0 ? `${diffMinutes}min` : 'Agora';
  } catch {
    return 'Recém-adicionado';
  }
};

// Helper to build WhatsApp URL
const getWhatsAppUrl = (telefone: string | undefined): string | null => {
  if (!telefone) return null;
  const cleanNumber = String(telefone).replace(/\D/g, '');
  if (!cleanNumber) return null;
  const fullNumber = cleanNumber.startsWith('55') ? cleanNumber : `55${cleanNumber}`;
  return `https://wa.me/${fullNumber}`;
};

export const LeadCard = ({ contact, onClick, viewMode = 'kanban', stageColor = '#7C3AED', clientId, isClosedSale = false }: LeadCardProps) => {
  const localScoreResult = useLeadScore(contact);
  const [score, setScore] = useState<number | null>(null);
  const [personaKey, setPersonaKey] = useState<string | null>(null);
  const [calculatingScore, setCalculatingScore] = useState(false);
  
  // Dynamic field resolution - support any column structure
  const displayName = contact.nome_cliente || contact.nome || contact.name || contact.nome_completo || '';
  const displayPhone = contact.telefone || contact.phone || contact.fone || '';
  const displayTemp = contact.temperatura_lead || contact.temperatura || contact.temp || '';
  const displayProduct = contact.categoria_produto || contact.produto || contact.category || '';
  const displayItem = contact.item_pedido || '';
  const displayBudget = contact.orcamento || contact.parcela || contact.valor || '';
  const displayMeetingDate = contact.data_hora_reuniao || contact.data_reuniao || '';
  const displayFirstAnalysis = contact.primeira_analise || '';
  const rawObs = contact.observacoes || contact.obs || '';
  // Merge observacoes + primeira_analise as informational text
  const displayObs = [rawObs, displayFirstAnalysis].filter(v => v && String(v).trim() !== '' && v !== 'true').join(' | ');
  const displayDateCreated = contact.data_primeira_mensagem || contact.criado_em || contact.created_at || '';
  const displayDateUpdated = contact.data_ultima_atualizacao || contact.atualizado_em || contact.updated_at || '';
  
  const temp = displayTemp?.toLowerCase() as LeadTemperature | undefined;
  const tempConfig = temp ? temperatureConfig[temp] : null;
  const tempColor = tempConfig?.color || stageColor;
  
  const timeAgo = getTimeAgo(displayDateUpdated);
  const whatsAppUrl = getWhatsAppUrl(displayPhone);

  const displayScore = score ?? localScoreResult.score;
  const personaFromScore = usePersona(contact, displayScore);
  const displayPersona = personaKey || personaFromScore.personaKey;

  // Load score/persona from persisted analysis; fallback stays local and reativo
  useEffect(() => {
    setScore(localScoreResult.score);
    setPersonaKey(personaFromScore.personaKey);

    if (clientId && (contact.session_id || contact.id)) {
      supabase
        .from('lead_analysis')
        .select('score, persona_key')
        .eq('client_id', clientId)
        .eq('lead_session_id', contact.session_id || contact.id)
        .maybeSingle()
        .then(({ data }) => {
          if (typeof data?.score === 'number') setScore(data.score);
          if (data?.persona_key) setPersonaKey(data.persona_key);
        });
    }
  }, [clientId, contact, localScoreResult.score, personaFromScore.personaKey]);

  // Calculate score for individual lead
  const handleCalculateScore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!clientId) return;
    
    setCalculatingScore(true);
    try {
      const leadData = {
        session_id: contact.session_id || contact.id,
        nome_cliente: contact.nome_cliente,
        telefone: contact.telefone,
        temperatura_lead: contact.temperatura_lead,
        etapa_atual: contact.etapa_atual,
        categoria_produto: contact.categoria_produto,
        data_primeira_mensagem: contact.data_primeira_mensagem,
        data_ultima_atualizacao: contact.data_ultima_atualizacao,
        observacoes: contact.observacoes,
      };

      const { error } = await supabase.functions.invoke('calculate-lead-scores', {
        body: { client_id: clientId, leads: [leadData] }
      });

      if (error) throw error;

      const { data: newScore } = await supabase
        .from('lead_analysis')
        .select('score, persona_key')
        .eq('client_id', clientId)
        .eq('lead_session_id', contact.session_id || contact.id)
        .maybeSingle();

      if (typeof newScore?.score === 'number') {
        setScore(newScore.score);
      }
      if (newScore?.persona_key) {
        setPersonaKey(newScore.persona_key);
      }
      toast.success('Score recalculado!');
    } catch (error: any) {
      console.error('Error calculating score:', error);
      toast.error('Erro ao calcular score');
    } finally {
      setCalculatingScore(false);
    }
  };

  const scoreStyle = getScoreStyle(displayScore);

  // List View Mode - Enhanced for mobile
  if (viewMode === 'list') {
    return (
      <div
        onClick={onClick}
        className="px-4 py-3 cursor-pointer transition-all hover:bg-secondary/50 active:bg-secondary/70"
      >
        <div className="flex items-center gap-3">
          {/* Left: Name and Phone */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-foreground truncate">
              {displayName || 'Sem nome'}
            </h3>
            {displayPhone && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <Phone size={10} className="text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground truncate">
                  {String(displayPhone)}
                </span>
              </div>
            )}
          </div>

          {/* Right: Badges */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Score badge */}
            {scoreStyle && (
              <span 
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border"
                style={{
                  background: scoreStyle.bg,
                  borderColor: scoreStyle.border,
                  color: scoreStyle.color
                }}
              >
                <Zap size={10} />
                {displayScore}
              </span>
            )}
            
            {/* Temperature badge */}
            {tempConfig && (
              <span 
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border"
                style={{
                  background: `${tempConfig.color}20`,
                  borderColor: `${tempConfig.color}40`,
                  color: tempConfig.color
                }}
              >
                {tempConfig.emoji}
              </span>
            )}

            {/* Persona badge */}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-muted/50 border-border text-foreground/80">
              {personaFromScore.persona.emoji} {personaFromScore.persona.label}
            </span>

            {/* WhatsApp quick action */}
            {whatsAppUrl && (
              <a
                href={whatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                style={{
                  background: 'rgba(37, 211, 102, 0.15)',
                  color: '#25D366',
                }}
                title="Abrir WhatsApp"
              >
                <WhatsAppIcon size={16} />
              </a>
            )}
          </div>
        </div>

        {/* Product and time info */}
        <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
          <span className="truncate flex items-center gap-1">
            {displayProduct && (
              <>
                <Package size={10} className="shrink-0" />
                {displayProduct}
              </>
            )}
          </span>
          {timeAgo && (
            <span className="flex items-center gap-1 shrink-0 ml-2">
              <Clock size={10} />
              {timeAgo}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // KANBAN VIEW - PREMIUM CARD (Responsive)
  // ==========================================
  return (
    <div 
      onClick={onClick}
      className="group relative mb-2 sm:mb-3 cursor-pointer transition-all duration-300 ease-out active:scale-[0.98]"
      style={{
        transform: 'translateY(0)',
      }}
      onMouseEnter={(e) => {
        const target = e.currentTarget as HTMLDivElement;
        target.style.transform = 'translateY(-4px) scale(1.02)';
      }}
      onMouseLeave={(e) => {
        const target = e.currentTarget as HTMLDivElement;
        target.style.transform = 'translateY(0) scale(1)';
      }}
    >
      {/* Main Card */}
      <div 
        className="relative rounded-xl overflow-hidden border transition-all duration-300"
        style={{
          background: 'linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--secondary) / 0.5) 100%)',
          borderColor: `${tempColor}30`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        }}
        onMouseEnter={(e) => {
          const target = e.currentTarget as HTMLDivElement;
          target.style.boxShadow = `0 12px 40px rgba(0, 0, 0, 0.4), 0 0 20px ${tempColor}30`;
          target.style.borderColor = `${tempColor}60`;
        }}
        onMouseLeave={(e) => {
          const target = e.currentTarget as HTMLDivElement;
          target.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
          target.style.borderColor = `${tempColor}30`;
        }}
      >
        {/* Left Color Bar */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 group-hover:w-1.5"
          style={{ 
            background: `linear-gradient(180deg, ${tempColor} 0%, ${stageColor} 100%)`
          }}
        />

        {/* Corner Glow Effect */}
        <div 
          className="absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-20 blur-xl transition-opacity group-hover:opacity-40"
          style={{ background: tempColor }}
        />

        {/* Content */}
        <div className="relative p-3 sm:p-4 pl-4 sm:pl-5">
          {/* Header */}
          <div className="flex justify-between items-start gap-2 mb-2 sm:mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-xs sm:text-sm font-semibold text-foreground leading-tight line-clamp-1 group-hover:text-primary transition-colors">
                {displayName || 'Sem nome'}
              </h3>
              {displayPhone && (
                <span className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Phone size={10} className="text-muted-foreground sm:w-3 sm:h-3" />
                  {String(displayPhone)}
                </span>
              )}
            </div>

            {tempConfig && (
              <span 
                className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-[10px] font-bold border shrink-0 transition-transform group-hover:scale-110"
                style={{
                  background: `${tempConfig.color}20`,
                  borderColor: `${tempConfig.color}40`,
                  color: tempConfig.color
                }}
              >
                {tempConfig.emoji} <span className="hidden sm:inline">{displayTemp}</span>
              </span>
            )}
          </div>

          {/* Score and Time in Stage Indicators */}
          <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3 flex-wrap">
            {/* Score Badge - Clickable to recalculate */}
            {scoreStyle ? (
              <button
                onClick={handleCalculateScore}
                disabled={calculatingScore}
                className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-[9px] sm:text-[10px] font-bold border transition-all hover:scale-105 hover:opacity-80 disabled:opacity-50"
                style={{
                  background: scoreStyle.bg,
                  borderColor: scoreStyle.border,
                  color: scoreStyle.color
                }}
                title="Clique para recalcular o score"
              >
                {calculatingScore ? (
                  <Loader2 size={9} className="animate-spin sm:w-2.5 sm:h-2.5" />
                ) : (
                  <Zap size={9} className="sm:w-2.5 sm:h-2.5" />
                )}
                <span className="hidden sm:inline">Score:</span> {displayScore}
              </button>
            ) : clientId && (
              <button
                onClick={handleCalculateScore}
                disabled={calculatingScore}
                className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-[9px] sm:text-[10px] font-medium border bg-primary/10 border-primary/30 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
              >
                {calculatingScore ? (
                  <Loader2 size={9} className="animate-spin sm:w-2.5 sm:h-2.5" />
                ) : (
                  <Zap size={9} className="sm:w-2.5 sm:h-2.5" />
                )}
                {calculatingScore ? '...' : <span className="hidden sm:inline">Calcular Score</span>}
                <span className="sm:hidden">Score</span>
              </button>
            )}

            <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md text-[9px] sm:text-[10px] font-semibold border bg-muted/50 border-border text-foreground/80">
              {personaFromScore.persona.emoji} {personaFromScore.persona.label}
            </span>
            
            {/* Time in Stage */}
            <div className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md bg-secondary/50">
              <Timer size={10} className="text-muted-foreground sm:w-3 sm:h-3" />
              <span className="text-[9px] sm:text-[10px] text-muted-foreground font-medium">
                {getTimeInStage(contact)} <span className="hidden sm:inline">nesta etapa</span>
              </span>
            </div>
          </div>

          {/* Recipient */}
          {contact.nome_destinatario && (
            <div className="mb-2 px-2 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
              <span className="text-xs text-primary flex items-center gap-1.5">
                <Gift size={12} />
                Para: {contact.nome_destinatario}
              </span>
            </div>
          )}

          {/* Product */}
          {(displayProduct || displayItem) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
              <Package size={12} className="shrink-0 text-primary/70" />
              <span className="truncate">
                {displayProduct}
                {displayItem && ` - ${displayItem}`}
              </span>
            </div>
          )}

          {/* Budget / Parcela */}
          {displayBudget && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
              <span className="text-primary/70 font-medium">R$</span>
              <span className="truncate font-medium">Parcela aprox: {displayBudget}</span>
            </div>
          )}

          {/* Meeting Date */}
          {displayMeetingDate && (
            <div className="flex items-center gap-1.5 text-xs mb-2 px-2 py-1 rounded-lg bg-primary/10 border border-primary/20">
              <Clock size={12} className="shrink-0 text-primary" />
              <span className="truncate text-primary font-medium">
                Reunião: {(() => {
                  try {
                    const d = new Date(displayMeetingDate);
                    if (isNaN(d.getTime())) return displayMeetingDate;
                    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                  } catch { return displayMeetingDate; }
                })()}
              </span>
            </div>
          )}

          {/* Observations */}
          {displayObs && (
            <div className="mb-3 p-2 rounded-lg bg-secondary/30 border border-border/20">
              <p className="text-[11px] text-muted-foreground italic line-clamp-2">
                {displayObs}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-2 border-t border-border/20 flex gap-2">
            {/* WhatsApp Action Button */}
            {whatsAppUrl && (
              <a
                href={whatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200"
                style={{
                  background: 'rgba(37, 211, 102, 0.15)',
                  border: '1px solid rgba(37, 211, 102, 0.3)',
                  color: '#25D366',
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget as HTMLAnchorElement;
                  target.style.background = 'rgba(37, 211, 102, 0.25)';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget as HTMLAnchorElement;
                  target.style.background = 'rgba(37, 211, 102, 0.15)';
                }}
                title="Abrir WhatsApp"
              >
                <WhatsAppIcon size={14} />
                WhatsApp
              </a>
            )}
          </div>

          {/* Footer with time ago */}
          {timeAgo && (
            <div className="flex justify-end mt-2">
              <span className="text-[9px] text-muted-foreground italic flex items-center gap-1">
                <Clock size={9} />
                {timeAgo}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
